import React from 'react';
import { Button, TextArea, TextInput } from '@app/components';
import { Toggle } from '@app/components/form/Toggle';
import { FieldWithLabel } from '@app/components/styled/form/Label';
import { StyledForms } from '@app/components/styled/form/Form';
import { IShop } from '@app/common';
import { CapacitySchedule } from './ProductionCapacitySlots';
import { Dropdown } from '@app/components/form/Dropdown';
import { useFieldArray, UseFieldArrayRemove, useWatch } from 'react-hook-form';
import { OrderModeSelect } from '@app/components/form/selects/features/OrderModeSelect';
import { createDefaultCapacityForDay } from './shop-form.helpers';
import { View } from 'react-native';
import { useState } from 'react';
import { NumberInput } from '@app/components/form/NumberInput';
import { FormSection } from '@app/components/styled/form/FormSection';
import { ScheduleForm } from '@app/components/form/ScheduleForm';
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { theme } from '@app/theme';
import { fiscalApi } from '@app/store';
import {
  CHAMPS_IDENTITE_FISCALE,
  boutiquePorteUneCaisse,
  champsFiscauxMalFormes,
  champsFiscauxManquants,
  libelleFiscal,
} from './shop-identite-fiscale';

// Même avertissement que la note des groupes de permissions (`RoleForm`), qui
// n'est pas exportée : un texte d'appoint en doré, à l'intérieur de la section,
// pour qu'il se lise comme une consigne et non comme une erreur de saisie.
const AvertissementFiscal = styled.Text({
  fontWeight: '600',
  fontSize: normalize(13),
  color: theme.colors.gold,
  letterSpacing: 0.2,
});

const socialMediaPlatform = [
  { platform: 'facebook', label: 'Facebook' },
  { platform: 'twitter', label: 'Twitter' },
  { platform: 'linkedin', label: 'Linkedin' },
  { platform: 'instagram', label: 'Instagram' },
];

const ProductionCapacityForm = ({
  index,
  remove,
}: {
  index: number;
  remove: UseFieldArrayRemove;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const productionCapacity = useWatch({
    name: `productionCapacities.${index}`,
  });

  const name = productionCapacity?.name
    ? `Capacité de production: ${productionCapacity.name}`
    : `Capacité de production: ${index + 1}`;

  return (
    <Dropdown label={name} isOpen={isOpen} handleOpen={setIsOpen} onDelete={() => remove(index)}>
      <TextInput name={`productionCapacities.${index}.name`} label="Nom" autoCapitalize="none" />

      <OrderModeSelect name={`productionCapacities.${index}.orderModes`} isMultiple />

      <View style={{ padding: 10 }}>
        <Toggle
          name={`productionCapacities.${index}.disabled`}
          options={[
            { title: 'Active', value: false },
            { title: 'Inactive', value: true },
          ]}
        />

        <CapacitySchedule index={index} />
      </View>
    </Dropdown>
  );
};

export const ShopForm = ({ shop }: { shop?: IShop }) => {
  const { append, remove, fields } = useFieldArray({
    name: 'productionCapacities',
  });

  // Les caisses sont demandées pour la boutique OUVERTE, jamais pour celle
  // sélectionnée dans la barre latérale : un administrateur édite n'importe
  // quelle boutique de l'organisation depuis la même liste. Sans ce `shopId`,
  // l'exigence n'apparaissait que lorsque les deux coïncidaient — c'est-à-dire
  // presque jamais sur les fiches qu'on vient corriger.
  //
  // L'identifiant vient de la fiche, pas du formulaire : ni les `defaultValues`
  // de la création ni `transformToUpdatableFormShop` ne portent `_id`, un
  // `useWatch` ne verrait donc jamais de boutique. `skip` sans identifiant : une
  // fiche en création n'a pas encore de caisse, et la gateway refuse la requête
  // sans en-tête `x-shop`.
  const { data: registres } = fiscalApi.useReadFiscalRegistersQuery(
    { shopId: shop?._id },
    { skip: !shop?._id },
  );

  const porteUneCaisse = boutiquePorteUneCaisse(registres, shop?._id);

  // Le SIRET est saisi plus haut, dans « Informations générales », mais sa forme
  // se contrôle avec le reste de l'identité : c'est la rubrique 13 de l'en-tête
  // R13, et un SIRET à treize chiffres part dans l'export aussi sûrement qu'un
  // NAF absent. Le serveur le refuse (garde `identite-fiscale.ts`) : mieux vaut
  // le dire à la saisie que sur un enregistrement rejeté.
  const [zipCode, city, naf, vatNumber, siret] = useWatch({
    name: ['zipCode', 'city', 'naf', 'vatNumber', 'siret'],
  });
  const identite = { zipCode, city, naf, vatNumber, siret };
  const manquants = champsFiscauxManquants(identite);
  const malFormes = champsFiscauxMalFormes(identite);

  const addProductionCapacity = () => {
    append({
      name: '',
      orderModes: [],
      tags: [],
      disabled: false,
      capacity: {
        monday: createDefaultCapacityForDay(shop, 'monday'),
        tuesday: createDefaultCapacityForDay(shop, 'tuesday'),
        wednesday: createDefaultCapacityForDay(shop, 'wednesday'),
        thursday: createDefaultCapacityForDay(shop, 'thursday'),
        friday: createDefaultCapacityForDay(shop, 'friday'),
        saturday: createDefaultCapacityForDay(shop, 'saturday'),
        sunday: createDefaultCapacityForDay(shop, 'sunday'),
      },
    });
  };

  return (
    <StyledForms.Form>
      <FormSection.Section>
        <FormSection.Title>Informations générales</FormSection.Title>
        <FormSection.Content>
          <TextInput name="publicName" label="Nom publique" autoCapitalize="none" />

          <TextInput name="internalName" label="Nom interne" autoCapitalize="none" />

          <TextArea name="description" label="Description" />

          <Toggle
            name="disabled"
            options={[
              { title: 'Actif', value: false },
              { title: 'Inactif', value: true },
            ]}
          />

          <TextInput name="address" label="Adresse" />

          <TextInput name="phone" label="Téléphone" />

          <TextInput name="email" label="E-mail" />

          <TextInput name="siret" label="Siret" />
        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Identité fiscale</FormSection.Title>
        <FormSection.Content>
          {porteUneCaisse && manquants.length > 0 && (
            <AvertissementFiscal testID="identite-fiscale-incomplete">
              Ce restaurant porte une caisse NF525 : ces informations figurent sur chaque ticket,
              dans l’archive fiscale et dans l’export remis à l’administration. Champs à compléter :{' '}
              {manquants.map(libelleFiscal).join(', ')}.
            </AvertissementFiscal>
          )}

          {malFormes.length > 0 && (
            <AvertissementFiscal testID="identite-fiscale-mal-formee">
              Format attendu — SIRET : quatorze chiffres ; code NAF / APE : quatre chiffres et
              une lettre (5610A) ; TVA intracommunautaire : FR suivi de onze chiffres. À revoir
              :{' '}
              {malFormes.map(libelleFiscal).join(', ')}.
            </AvertissementFiscal>
          )}

          {CHAMPS_IDENTITE_FISCALE.map(({ name, label }) => (
            <TextInput
              key={name}
              name={name}
              label={porteUneCaisse ? `${label} *` : label}
              autoCapitalize="none"
            />
          ))}
        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Informations avancées</FormSection.Title>
        <FormSection.Content>
          <TextArea name="eshopMessage" label="Message sur la boutique en ligne" />

          <FieldWithLabel label="Envoi automatique du résumé de service par e-mail">
            <Toggle
              name="skipServiceSummaryEmail"
              options={[
                { title: 'Oui', value: false },
                { title: 'Non', value: true },
              ]}
            />
          </FieldWithLabel>

          <NumberInput name="location.coordinates[1]" label="Latitude du restaurant" />

          <NumberInput name="location.coordinates[0]" label="Longitude du restaurant" />

          <Toggle
            name="isDeliveryOpen"
            options={[
              { title: 'Ouvert en livraison', value: true },
              { title: 'Fermé en livraison', value: false },
            ]}
          />

          <Toggle
            name="isTakeawayOpen"
            options={[
              { title: 'Ouvert à emporter', value: true },
              { title: 'Fermé à emporter', value: false },
            ]}
          />

          <Toggle
            name="enableDeliveryTracking"
            options={[
              { title: 'Tracking livreur actif', value: true },
              { title: 'Tracking livreur inactif', value: false },
            ]}
          />
        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Horaires et délais</FormSection.Title>
        <FormSection.Content>
          <ScheduleForm
            label="Horaires à emporter"
            names={{
              schedule: 'takeAwaySchedule',
              selectedDays: 'takeAwaySelectedDays',
              startTime: 'takeAwayStartTime',
              endTime: 'takeAwayEndTime',
            }}
          />

          <ScheduleForm
            label="Horaires en livraison"
            names={{
              schedule: 'deliverySchedule',
              selectedDays: 'deliverySelectedDays',
              startTime: 'deliveryStartTime',
              endTime: 'deliveryEndTime',
            }}
          />

          <ScheduleForm
            label="Horaires sur plateformes"
            names={{
              schedule: 'platformsSchedule',
              selectedDays: 'platformsSelectedDays',
              startTime: 'platformsStartTime',
              endTime: 'platformsEndTime',
            }}
          />

          <NumberInput name="takeAwayTime" label="Délais à emporter" />

          <NumberInput name="deliveryTime" label="Délais en livraison" />
        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Réseaux sociaux</FormSection.Title>
        <FormSection.Content>
          {socialMediaPlatform.map((socialMedia, index) => {
            return (
              <TextInput
                key={index}
                name={`socialMediaLinks.${socialMedia.platform}`}
                label={`Lien ${socialMedia.label}`}
              />
            );
          })}
        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Capacités de production</FormSection.Title>
        <FormSection.Content>
          {fields.map((field, index) => (
            <ProductionCapacityForm key={field.id} index={index} remove={remove} />
          ))}

          <Button
            onPress={addProductionCapacity}
            title="Ajouter une capacité de production"
            bordered
            grow
            large
            square
            smallText
          />
        </FormSection.Content>
      </FormSection.Section>
    </StyledForms.Form>
  );
};
